import React, { useCallback, useState, useEffect, FC } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Resources, AppPermissions } from '@erecruit/types';
import { Intl } from '@erecruit/core';
import {
  CardOverview,
  Container,
  Flex,
  FlexItem,
  H2,
  Spinner,
  Accordion,
  styled,
} from '@erecruit/ui-kit';
import { Authorize } from '@erecruit/modules';

import GeneralSection from './GeneralSection';
import BillingSection from './BillingSection';
import ScheduleSection from './ScheduleSection';
import PeopleSection from './PeopleSection';
import AttachmentsSection from './AttachmentsSection';
import AdditionalInfoSection from './AdditionalInfoSection';

import { RootState } from '../../../../redux/reducers';
import { getLoggedUserGuid } from '../../../../App/selectors';
import {
  approveApprovalPath,
  fetchMatchRecord,
  rejectApprovalPath,
  selectors,
  saveMatchRecord,
} from '../../state/reducer';
import MatchTimeline from '../MatchTimeline';
import { ApprovalPath } from '../../../ApprovalPath';

import {
  MatchRecordSections,
  MatchRecordActions,
  MatchRecordSection,
  SupportedMatchEditSection,
  SaveMatchRecordPayloadProps,
} from '../../types';
import { EditMatchModal } from '../EditMatchModal';

const { FormattedMessage } = Intl;

const BackgroundContainer = styled.div(({ theme: { newColors } }) => ({
  backgroundColor: newColors.bgStandard,
  marginRight: 0,
}));

const ApprovalPathContainer = styled(Flex)({
  marginTop: 16,
});

const OverviewContainer = styled(Container)({
  background: 'transparent',
  marginTop: 40,
});

const SpinnerContainer = styled(Container)({
  padding: '300px 0',
  textAlign: 'center',
});

export interface MatchOverviewProps
  extends Pick<
    MatchRecordActions,
    'fetchMatchRecord' | 'approveApprovalPath' | 'rejectApprovalPath'
  > {
  isLoadingRecord: boolean;
  isUpdating: boolean;
  sections?: MatchRecordSections;
  onMatchAction(matchActionType: Resources.MatchActionType): void;
  userGuid: string;
  saveMatchRecord: typeof saveMatchRecord;
}

export const MatchOverviewComponent: FC<MatchOverviewProps> = props => {
  const {
    approveApprovalPath,
    fetchMatchRecord,
    isLoadingRecord,
    isUpdating,
    onMatchAction,
    rejectApprovalPath,
    sections,
    userGuid,
    saveMatchRecord,
  } = props;

  const { id } = useParams();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editModalSection, setEditModalSection] = useState<
    MatchRecordSection | undefined
  >(undefined);

  useEffect(() => {
    fetchMatchRecord(Number(id));
  }, [fetchMatchRecord, id]);

  const handleSaveMatch = useCallback(
    (value: SupportedMatchEditSection) => {
      if (!id) return;

      const data: SaveMatchRecordPayloadProps = {
        matchId: Number(id),
        mutation: value,
      };

      saveMatchRecord(data);
      setEditModalVisible(false);
    },
    [saveMatchRecord, id],
  );

  const handleEdit = useCallback(
    (section: MatchRecordSection) => {
      if (!sections) return;
      setEditModalSection(section);
      setEditModalVisible(true);
    },
    [sections],
  );

  if (isLoadingRecord) {
    return (
      <SpinnerContainer gutterDirection="vertical" background="pageSecondary">
        <Spinner />
      </SpinnerContainer>
    );
  }

  if (!sections) return null;

  return (
    <>
      {editModalSection && (
        <EditMatchModal
          section={editModalSection}
          sections={sections}
          visible={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          onSave={handleSaveMatch}
        />
      )}
      <Container gutter={4} stretch={false} background="pageSecondary">
        <BackgroundContainer>
          {sections.approvalPath ? (
            <Container padding={[4, 4, 10, 4]}>
              <Accordion
                defaultItem={
                  <ApprovalPathContainer>
                    <ApprovalPath
                      approvalPath={sections.approvalPath}
                      userGuid={userGuid}
                      onApproveClick={(approvalPathId, note) =>
                        approveApprovalPath({ approvalPathId, note })
                      }
                      onRejectClick={(
                        approvalPathId,
                        rejectionReasonId,
                        note,
                      ) =>
                        rejectApprovalPath({
                          approvalPathId,
                          note,
                          rejectionReasonId,
                        })
                      }
                      rejectionReasons={sections.approvalRejectionReasons}
                    />
                  </ApprovalPathContainer>
                }
                expandedItem={
                  sections.steps.length ? (
                    <Container padding={[0, 0, 4, 0]}>
                      <MatchTimeline
                        actions={sections.actions}
                        isUpdating={isUpdating}
                        match={sections.general}
                        matchStatus={sections.matchStatus}
                        onMatchAction={onMatchAction}
                        steps={sections.steps}
                      />
                    </Container>
                  ) : null
                }
                buttonText={(collapsed: boolean) =>
                  collapsed ? (
                    <FormattedMessage
                      id={messageIds.vms.matchRecord.accordionCollapsedText}
                    />
                  ) : (
                    <FormattedMessage
                      id={messageIds.vms.matchRecord.accordionExpandedText}
                    />
                  )
                }
              />
            </Container>
          ) : sections.steps.length ? (
            <MatchTimeline
              actions={sections.actions}
              isUpdating={isUpdating}
              match={sections.general}
              matchStatus={sections.matchStatus}
              onMatchAction={onMatchAction}
              steps={sections.steps}
            />
          ) : null}
        </BackgroundContainer>
        <OverviewContainer gutter={4} stretch padding={12}>
          <CardOverview
            title={
              <H2 color="headerPrimary">
                <FormattedMessage
                  id={messageIds.vms.matchRecord.sectionsTitle}
                />
              </H2>
            }
            extraComponent={null}
          >
            <Flex gutter={4}>
              <FlexItem basis="50%">
                <Flex direction="column" gutter={4} gutterDirection="vertical">
                  <GeneralSection data={sections.general} />
                  <Authorize permission={AppPermissions.ViewAttachments}>
                    <AttachmentsSection data={sections.attachments} />
                  </Authorize>
                  {sections.additionalInfo.length > 0 && (
                    <Authorize
                      permission={AppPermissions.ViewMatchAdditionalInfo}
                    >
                      <AdditionalInfoSection
                        data={sections.additionalInfo}
                        onEdit={handleEdit}
                      />
                    </Authorize>
                  )}
                </Flex>
              </FlexItem>
              <FlexItem basis="50%">
                <Flex direction="column" gutter={4} gutterDirection="vertical">
                  <Authorize permission={AppPermissions.ViewPositionBilling}>
                    <BillingSection data={sections.billing} />
                  </Authorize>
                  <Authorize permission={AppPermissions.ViewPositionSchedule}>
                    <ScheduleSection data={sections.schedule} />
                  </Authorize>
                  <PeopleSection data={sections.people} />
                </Flex>
              </FlexItem>
            </Flex>
          </CardOverview>
        </OverviewContainer>
      </Container>
    </>
  );
};

export default connect(
  (state: RootState) => ({
    isLoadingRecord: selectors.selectMatchRecord(state).isLoadingRecord,
    isUpdating: selectors.selectMatchRecord(state).isUpdating,
    sections: selectors.selectMatchRecord(state).sections,
    userGuid: getLoggedUserGuid(state),
  }),
  {
    approveApprovalPath,
    fetchMatchRecord,
    rejectApprovalPath,
    saveMatchRecord,
  },
)(MatchOverviewComponent);
